import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  BarChart3,
  DollarSign,
  TrendingUp,
  Calendar,
  Package,
} from 'lucide-react';

const DashboardPanels = ({ instruments }) => {
  // Calculate cost statistics
  const totalCalibracao = instruments.reduce((sum, inst) => sum + (inst.custoCalibracao || 0), 0);
  const totalManutencao = instruments.reduce((sum, inst) => sum + (inst.custoManutencao || 0), 0);
  const totalCustos = totalCalibracao + totalManutencao;

  // Calculate annual costs (assuming 1 calibration per year)
  const custoAnualCalibracao = totalCalibracao;
  const custoAnualManutencao = totalManutencao;
  const custoAnualTotal = custoAnualCalibracao + custoAnualManutencao;

  // Group costs by instrument type
  const custosPorTipo = instruments.reduce((acc, inst) => {
    if (!acc[inst.tipo]) {
      acc[inst.tipo] = {
        calibracao: 0,
        manutencao: 0,
        total: 0,
      };
    }
    acc[inst.tipo].calibracao += inst.custoCalibracao || 0;
    acc[inst.tipo].manutencao += inst.custoManutencao || 0;
    acc[inst.tipo].total += (inst.custoCalibracao || 0) + (inst.custoManutencao || 0);
    return acc;
  }, {});

  const custosArray = Object.entries(custosPorTipo)
    .map(([tipo, custos]) => ({ tipo, ...custos }))
    .sort((a, b) => b.total - a.total);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const maxCusto = Math.max(...custosArray.map((c) => c.total), 1);

  return (
    <div className="space-y-6">
      {/* Cost Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-orange-900">Custo Total</CardTitle>
            <DollarSign className="w-5 h-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-900">{formatCurrency(totalCustos)}</div>
            <p className="text-xs text-orange-700 mt-1">Calibração + Manutenção</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-amber-900">Custo de Calibração</CardTitle>
            <TrendingUp className="w-5 h-5 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-900">{formatCurrency(totalCalibracao)}</div>
            <p className="text-xs text-amber-700 mt-1">Total de calibrações</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-yellow-100">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-yellow-900">Custo de Manutenção</CardTitle>
            <BarChart3 className="w-5 h-5 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-900">{formatCurrency(totalManutencao)}</div>
            <p className="text-xs text-yellow-700 mt-1">Total de manutenções</p>
          </CardContent>
        </Card>
      </div>

      {/* Bar Chart: Annual Costs */}
      <Card className="border-2 border-orange-300 bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <BarChart3 className="w-6 h-6 text-orange-600" />
            Gráfico de Custos Anuais
          </CardTitle>
          <CardDescription>Comparação visual dos custos anuais estimados</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Bar Chart */}
            <div className="flex items-end justify-around h-64 border-b-2 border-l-2 border-slate-300 p-4">
              {/* Calibração Bar */}
              <div className="flex flex-col items-center justify-end flex-1 mx-2">
                <div className="w-full bg-gradient-to-t from-orange-500 to-orange-400 rounded-t-lg shadow-lg relative group transition-all hover:from-orange-600 hover:to-orange-500"
                     style={{ 
                       height: `${custoAnualTotal > 0 ? (custoAnualCalibracao / custoAnualTotal) * 100 : 0}%`,
                       minHeight: '40px'
                     }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {formatCurrency(custoAnualCalibracao)}
                    </span>
                  </div>
                </div>
                <p className="text-sm font-semibold text-slate-900 mt-2">Calibração</p>
              </div>

              {/* Manutenção Bar */}
              <div className="flex flex-col items-center justify-end flex-1 mx-2">
                <div className="w-full bg-gradient-to-t from-amber-500 to-amber-400 rounded-t-lg shadow-lg relative group transition-all hover:from-amber-600 hover:to-amber-500"
                     style={{ 
                       height: `${custoAnualTotal > 0 ? (custoAnualManutencao / custoAnualTotal) * 100 : 0}%`,
                       minHeight: '40px'
                     }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {formatCurrency(custoAnualManutencao)}
                    </span>
                  </div>
                </div>
                <p className="text-sm font-semibold text-slate-900 mt-2">Manutenção</p>
              </div>

              {/* Total Bar */}
              <div className="flex flex-col items-center justify-end flex-1 mx-2">
                <div className="w-full bg-gradient-to-t from-black via-slate-800 to-slate-700 rounded-t-lg shadow-lg relative group transition-all hover:from-slate-900 hover:to-slate-800"
                     style={{ 
                       height: '100%',
                       minHeight: '40px'
                     }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {formatCurrency(custoAnualTotal)}
                    </span>
                  </div>
                </div>
                <p className="text-sm font-semibold text-black mt-2">Total</p>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-700 font-medium mb-1">Percentual</p>
                <p className="text-lg font-bold text-orange-900">
                  {custoAnualTotal > 0 ? ((custoAnualCalibracao / custoAnualTotal) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-700 font-medium mb-1">Percentual</p>
                <p className="text-lg font-bold text-amber-900">
                  {custoAnualTotal > 0 ? ((custoAnualManutencao / custoAnualTotal) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="text-center p-3 bg-slate-100 rounded-lg border-2 border-black">
                <p className="text-xs text-slate-700 font-medium mb-1">Total Anual</p>
                <p className="text-lg font-bold text-black">100%</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3D Pie Chart: Calibration vs Maintenance Costs */}
      <Card className="border-2 border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <BarChart3 className="w-6 h-6 text-orange-600" />
            Gráfico de Pizza 3D - Custos de Calibração e Manutenção
          </CardTitle>
          <CardDescription>Distribuição percentual dos custos totais</CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            const pieData = [
              { 
                name: 'Calibração', 
                value: totalCalibracao,
                color: '#1e3a8a', // blue-900 (azul marinho)
                label: 'Custos de Calibração'
              },
              { 
                name: 'Manutenção', 
                value: totalManutencao,
                color: '#dc2626', // red-600 (vermelho)
                label: 'Custos de Manutenção'
              },
            ];

            const RADIAN = Math.PI / 180;
            const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
              const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill="white"
                  textAnchor={x > cx ? 'start' : 'end'}
                  dominantBaseline="central"
                  className="font-bold text-sm"
                >
                  {`${(percent * 100).toFixed(1)}%`}
                </text>
              );
            };

            return (
              <div className="space-y-6">
                {/* 3D Pie Chart using Recharts */}
                <div className="flex justify-center items-center py-6">
                  <div className="relative">
                    {/* Custom 3D Pie Chart using CSS */}
                    <div className="flex justify-center items-center h-80 w-full">
                      <div className="relative w-80 h-80">
                        {/* Base shadow for 3D effect */}
                        <div className="absolute top-8 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 opacity-30 blur-xl" />
                        
                        {/* Pie segments */}
                        <svg viewBox="0 0 200 200" className="absolute top-0 left-0 w-full h-full drop-shadow-2xl">
                          {(() => {
                            const total = totalCalibracao + totalManutencao;
                            const calibracaoPercent = (totalCalibracao / total) * 100;
                            const manutencaoPercent = (totalManutencao / total) * 100;
                            
                            const calibracaoAngle = (calibracaoPercent / 100) * 360;
                            const manutencaoAngle = (manutencaoPercent / 100) * 360;
                            
                            // Helper function to create arc path
                            const createArc = (startAngle, endAngle) => {
                              const start = polarToCartesian(100, 100, 80, endAngle);
                              const end = polarToCartesian(100, 100, 80, startAngle);
                              const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
                              return [
                                "M", 100, 100,
                                "L", start.x, start.y,
                                "A", 80, 80, 0, largeArcFlag, 0, end.x, end.y,
                                "Z"
                              ].join(" ");
                            };
                            
                            function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
                              const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
                              return {
                                x: centerX + (radius * Math.cos(angleInRadians)),
                                y: centerY + (radius * Math.sin(angleInRadians))
                              };
                            }
                            
                            return (
                              <>
                                {/* Calibração slice */}
                                <path
                                  d={createArc(0, calibracaoAngle)}
                                  fill="#1e3a8a"
                                  stroke="#fff"
                                  strokeWidth="2"
                                  className="transition-all hover:opacity-80 cursor-pointer"
                                  style={{
                                    filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))',
                                  }}
                                />
                                
                                {/* Manutenção slice */}
                                <path
                                  d={createArc(calibracaoAngle, 360)}
                                  fill="#dc2626"
                                  stroke="#fff"
                                  strokeWidth="2"
                                  className="transition-all hover:opacity-80 cursor-pointer"
                                  style={{
                                    filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))',
                                  }}
                                />
                                
                                {/* Center circle for 3D depth */}
                                <circle
                                  cx="100"
                                  cy="100"
                                  r="35"
                                  fill="url(#centerGradient)"
                                  stroke="#fff"
                                  strokeWidth="2"
                                />
                                
                                {/* Gradient definition */}
                                <defs>
                                  <radialGradient id="centerGradient">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#1e3a8a" />
                                  </radialGradient>
                                </defs>
                                
                                {/* Percentage labels */}
                                <text
                                  x={100 + 40 * Math.cos((calibracaoAngle / 2 - 90) * Math.PI / 180)}
                                  y={100 + 40 * Math.sin((calibracaoAngle / 2 - 90) * Math.PI / 180)}
                                  fill="white"
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  className="font-bold text-lg"
                                  style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
                                >
                                  {calibracaoPercent.toFixed(1)}%
                                </text>
                                
                                <text
                                  x={100 + 40 * Math.cos((calibracaoAngle + manutencaoAngle / 2 - 90) * Math.PI / 180)}
                                  y={100 + 40 * Math.sin((calibracaoAngle + manutencaoAngle / 2 - 90) * Math.PI / 180)}
                                  fill="white"
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  className="font-bold text-lg"
                                  style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
                                >
                                  {manutencaoPercent.toFixed(1)}%
                                </text>
                              </>
                            );
                          })()}
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legend and Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border-2 border-blue-900 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-6 h-6 rounded bg-blue-900" />
                      <h4 className="font-bold text-blue-900 text-lg">Calibração</h4>
                    </div>
                    <p className="text-3xl font-bold text-blue-900 mb-2">{formatCurrency(totalCalibracao)}</p>
                    <p className="text-sm text-blue-800">
                      {((totalCalibracao / totalCustos) * 100).toFixed(1)}% do custo total
                    </p>
                    <p className="text-xs text-slate-600 mt-2">
                      Média por instrumento: {formatCurrency(instruments.length > 0 ? totalCalibracao / instruments.length : 0)}
                    </p>
                  </div>

                  <div className="bg-white border-2 border-red-600 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-6 h-6 rounded bg-red-600" />
                      <h4 className="font-bold text-red-900 text-lg">Manutenção</h4>
                    </div>
                    <p className="text-3xl font-bold text-red-700 mb-2">{formatCurrency(totalManutencao)}</p>
                    <p className="text-sm text-red-600">
                      {((totalManutencao / totalCustos) * 100).toFixed(1)}% do custo total
                    </p>
                    <p className="text-xs text-slate-600 mt-2">
                      Média por instrumento: {formatCurrency(instruments.length > 0 ? totalManutencao / instruments.length : 0)}
                    </p>
                  </div>
                </div>

                {/* Total Summary */}
                <div className="bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-400 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg mb-2">Custo Total</h4>
                      <p className="text-4xl font-bold text-orange-700">{formatCurrency(totalCustos)}</p>
                    </div>
                    <DollarSign className="w-16 h-16 text-orange-500 opacity-30" />
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t-2 border-orange-300">
                    <div>
                      <p className="text-xs text-slate-600">Instrumentos</p>
                      <p className="text-xl font-bold text-slate-900">{instruments.length}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600">Média Geral</p>
                      <p className="text-xl font-bold text-slate-900">
                        {formatCurrency(instruments.length > 0 ? totalCustos / instruments.length : 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600">Custos Anuais</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(totalCustos)}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPanels;
